import { useCallback, useEffect, useState } from 'react';
import type { Key } from 'react-aria-components';

import type { CrmCreateSection } from './types';

export interface UseAccordionErrorExpansionParams {
  sections: CrmCreateSection[];
  sectionStyle: 'flat' | 'accordion' | undefined;
  visibleErrors: Record<string, string | undefined>;
}

export interface UseAccordionErrorExpansionResult {
  expandedKeys: Set<Key>;
  handleExpandedChange: (keys: Set<Key>) => void;
}

/**
 * Accordion expand state (only meaningful for sectionStyle 'accordion').
 * React-aria's Accordion enforces single-expand, so `expandedKeys` holds at
 * most one key.
 *
 * Also auto-expands the section containing a hidden, visible-to-the-user
 * error so it stays reachable. Guards prevent an update loop: bail when
 * there are no erroring sections, and bail when an already-expanded section
 * covers one of them.
 */
export function useAccordionErrorExpansion({
  sections,
  sectionStyle,
  visibleErrors,
}: UseAccordionErrorExpansionParams): UseAccordionErrorExpansionResult {
  const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(() => {
    const first = sections.find(s => s.defaultExpanded);
    return new Set(first ? [first.id] : []);
  });
  const handleExpandedChange = useCallback(
    (keys: Set<Key>) => setExpandedKeys(keys),
    []
  );

  useEffect(() => {
    if (sectionStyle !== 'accordion') {
      return;
    }
    const erroringSectionIds = sections
      .filter(section =>
        section.fields.some(
          field => field.kind !== 'association' && visibleErrors[field.property]
        )
      )
      .map(section => section.id);
    if (erroringSectionIds.length === 0) {
      return;
    }
    if (
      Array.from(expandedKeys).some(key =>
        erroringSectionIds.includes(String(key))
      )
    ) {
      return;
    }
    setExpandedKeys(new Set([erroringSectionIds[0]]));
  }, [visibleErrors, sections, sectionStyle, expandedKeys]);

  return { expandedKeys, handleExpandedChange };
}
